import { waHandoff } from './funnelTrack';
// @ts-ignore — plain-JS module shared with scripts/wa-worker.mjs
import { guardMessage } from './msgGuard.mjs';
import { getSessionId } from './trackerSession';
import { abCtx } from './abClient';
// ── Single source of truth for the WhatsApp message + contact copy ──────────
// Used by WaSmartButton, ProSmartIntake, WAButton and ContactModal so that any
// future wording change propagates to every button automatically.

export type WaCtx = {
  categorySlug?: string | null;
  categoryName?: string | null;
  city?: string | null;
  cleanNeed?: string | null;
  priceHint?: string | null;
  timingHint?: string | null;
  customNeed?: string | null;
} | null;

/** Strips astral-plane characters (code point ≥ U+10000 — every modern pictographic
 *  emoji: 👋🔒💬📞✨ etc, plus their variation-selector/ZWJ leftovers) from text bound
 *  for an actual WhatsApp send. 2026-08-18 (user-caught live): WhatsApp's own web
 *  prefill (wa.me / api.whatsapp.com/send) mangles these into U+FFFD ("�") in the
 *  composed message on some clients — found on a pro's phone via a real inquiry link.
 *  BMP symbols (★, ✓, —) render fine and are left alone; this is the ONE place that
 *  distinction gets enforced. Call this at the FINAL point text becomes WhatsApp-bound
 *  (openWhatsApp() below, and the server-side /wa/<code> redirect + link-creation in
 *  lib/waLinks.ts) rather than hunting down every template that might add an emoji —
 *  that's what let 👋 slip back in here even after the exact same lesson was already
 *  learned once for lib/waLinks.ts's composeNeedSentence(). */
export function stripAstralEmoji(text: string): string {
  return Array.from(text)
    .filter(ch => (ch.codePointAt(0) ?? 0) < 0x10000)
    .join('')
    .replace(/[\u200D\uFE0F]/g, '') // orphaned zero-width-joiner / variation selectors
    .replace(/[ \t]{2,}/g, ' ');
}

/** Pro's first name only — never expose the family name in messages. */
export function proFirstName(name: string, first?: string | null): string {
  return (first && first.trim()) || name.split(/[\s\-,]+/)[0];
}

// ── Category title → service noun ───────────────────────────────────────────
// he_categories.name_he is the PROFESSIONAL TITLE (e.g. "שמאי מקרקעין" = the person).
// In a message "לגבי {X}" needs the SERVICE noun, not the title:
//   "לגבי שמאי מקרקעין" ✗  →  "לגבי שמאות מקרקעין" ✓
// Mapped categories get their proper noun; everything else falls back to the
// always-grammatical "שירותי {title}" ("לגבי שירותי חשמלאי" etc.).
const SERVICE_NOUN: Record<string, string> = {
  'אדריכל': 'תכנון אדריכלי',
  'אופטומטריסט': 'בדיקות ראייה',
  'אורתודנט': 'יישור שיניים',
  'אינסטלטור': 'אינסטלציה',
  'איש ייבוש תת רצפתי': 'ייבוש תת-רצפתי',
  'איש עבודות בגובה': 'עבודות בגובה',
  'אמבטיונר': 'חידוש אמבטיות',
  'בודק חשמל': 'בדיקת חשמל',
  'בודק קרינה': 'בדיקת קרינה',
  'בונה אקווריומים ובריכות נוי': 'בניית אקווריומים ובריכות נוי',
  'בית דפוס': 'שירותי דפוס',
  'בניית בריכות': 'בניית בריכות',
  'גגן': 'עבודות גגות',
  'גוזם עצים': 'גיזום עצים',
  'גנן': 'גינון',
  'דולה': 'ליווי לידה',
  'דיאטנית': 'ייעוץ תזונה',
  'דיטיילינג וציפויים לרכב': 'דיטיילינג וציפויים לרכב',
  'דלתות פנים': 'התקנת דלתות פנים',
  'הנדימן': 'עבודות תחזוקה ותיקונים',
  'וטרינר': 'טיפול וטרינרי',
  'זגג': 'עבודות זיגוג',
  'זגג רכב': 'זיגוג רכב',
  'חברה להשכרת ציוד לאירועים': 'השכרת ציוד לאירועים',
  'חברת אמבולנס פרטי': 'שירותי אמבולנס פרטי',
  'חברת בדק בית': 'בדק בית',
  'חברת ביובית': 'שירותי ביוב',
  'חברת דיגיטציה': 'שירותי דיגיטציה',
  'חברת הסעות': 'שירותי הסעות',
  'חברת מטבחים': 'תכנון והתקנת מטבחים',
  'חברת ניהול ואחזקת מבנים': 'ניהול ואחזקת מבנים',
  'חברת ניקוי ספות': 'ניקוי ספות',
  'חברת ניקיון ופוליש': 'ניקיון ופוליש',
  'חברת קייטרינג': 'קייטרינג',
  'חברת שיקום מבנים': 'שיקום מבנים',
  'חשמלאי': 'עבודות חשמל',
  'חשמלאי רכב': 'חשמלאות רכב',
  'טכנאי אזעקות ומצלמות': 'התקנת אזעקות ומצלמות',
  'טכנאי אינטרקום וקודנים': 'התקנת אינטרקום וקודנים',
  'טכנאי גז': 'התקנה וטיפול בגז',
  'טכנאי דוד שמש': 'טיפול בדוד שמש',
  'טכנאי טלוויזיה': 'תיקון טלוויזיות',
  'טכנאי מוצרי חשמל': 'תיקון מוצרי חשמל',
  'טכנאי מזגנים': 'טיפול והתקנת מזגנים',
  'טכנאי מחשבים': 'תיקון מחשבים',
  'טכנאי מערכות סולאריות': 'התקנת מערכות סולאריות',
  'טכנאי מקרר': 'תיקון מקררים',
  'טכנאי רשתות': 'התקנת רשתות תקשורת',
  'טכנאי שערים חשמליים': 'התקנת שערים חשמליים',
  'יועץ מס': 'ייעוץ מס',
  'יועץ משכנתאות': 'ייעוץ משכנתאות',
  'יועצת זוגית': 'ייעוץ זוגי',
  'יועצת שינה': 'ייעוץ שינה',
  'מאלף כלבים': 'אילוף כלבים',
  'מאמן כושר': 'אימון כושר',
  'מאפרת ומעצבת שיער': 'איפור ועיצוב שיער',
  'מאתר נזילות': 'איתור נזילות',
  'מדביק טפטים': 'הדבקת טפטים',
  'מדביר': 'הדברה',
  'מדריכת הורים': 'הדרכת הורים',
  'מדריכת הנקה': 'הדרכת הנקה',
  'מוביל': 'הובלה',
  'מוביל להובלות קטנות': 'הובלות קטנות',
  'מודד מוסמך': 'מדידות',
  'מוהל': 'ברית מילה',
  'מוסך': 'תיקוני רכב',
  'מוסך נייד': 'תיקוני רכב ניידים',
  'מוסך קטנועים': 'תיקון קטנועים',
  'מורה לנהיגה': 'שיעורי נהיגה',
  'מטפל לדיקור סיני': 'דיקור סיני',
  'מטפלת אקוויליבריו': 'טיפול אקוויליבריו',
  'מיטרנט': 'שירותי מיטרנט',
  'מכוון פסנתרים': 'כיוון פסנתרים',
  'מכון גירים': 'תיקון גירים',
  'מנהל חשבונות': 'הנהלת חשבונות',
  'מנהלת ומפיקת אירועים': 'ניהול והפקת אירועים',
  'מנסר וקודח בטון': 'ניסור וקידוח בטון',
  'מנעולן': 'פתיחת והחלפת מנעולים',
  'מנעולן רכב': 'מנעולנות רכב',
  'מסגר': 'עבודות מסגרות',
  'מסדר בתים': 'סידור וארגון הבית',
  'מעבדת סלולר': 'תיקון מכשירי סלולר',
  'מעסה מקצועי': 'עיסוי מקצועי',
  'מעצב גרפי': 'עיצוב גרפי',
  'מעצב פנים': 'עיצוב פנים',
  'מעצבת אירועים': 'עיצוב אירועים',
  'מפנה דירות': 'פינוי דירות',
  'מפעיל בריכות': 'תחזוקת בריכות',
  'מפעיל ימי הולדת': 'הפעלת ימי הולדת',
  'מפעיל מנוף': 'שירותי מנוף',
  'מפקח בנייה': 'פיקוח בנייה',
  'מצפה דלתות': 'ציפוי דלתות',
  'מקים בריכות שחייה': 'הקמת בריכות שחייה',
  'מקים גינות': 'הקמת גינות',
  'מרחיק יונים': 'הרחקת יונים',
  'מתווך': 'תיווך נדל״ן',
  'מתקין דלתות': 'התקנת דלתות',
  'מתקין מערכות חימום': 'התקנת מערכות חימום',
  'מתקין מערכות כיבוי אש': 'התקנת מערכות כיבוי אש',
  'מתקין מערכות לרכב': 'התקנת מערכות לרכב',
  'מתקין מקלחונים': 'התקנת מקלחונים',
  'מתקין סוככים': 'התקנת סוככים',
  'מתקין פרגולות אלומיניום': 'התקנת פרגולות אלומיניום',
  'מתקין פרקטים': 'התקנת פרקטים',
  'מתקין תריסים': 'התקנת תריסים',
  'נגר': 'עבודות נגרות',
  'נגר למטבחים': 'נגרות מטבחים',
  'נגר פרגולות ודקים': 'נגרות פרגולות ודקים',
  'נוטריון': 'שירותי נוטריון',
  'נטורופת': 'נטורופתיה',
  'סוכן ביטוח': 'ייעוץ ביטוח',
  'ספק אטרקציות לאירועים': 'אטרקציות לאירועים',
  'ספק מגשי אירוח': 'מגשי אירוח',
  'ספר כלבים': 'טיפוח כלבים',
  'עורך דין': 'ייעוץ משפטי',
  'עורך דין חובות': 'ייעוץ משפטי בתחום חובות והוצאה לפועל',
  'עורך דין מסחרי': 'ייעוץ משפטי מסחרי',
  'עורך דין מקרקעין': 'ייעוץ משפטי בתחום המקרקעין',
  'עורך דין משפחה': 'ייעוץ משפטי בדיני משפחה',
  'עורך דין נזיקין': 'ייעוץ משפטי בתחום הנזיקין',
  'עורך דין עבודה': 'ייעוץ משפטי בדיני עבודה',
  'עורך דין תעבורה': 'ייעוץ משפטי בתחום התעבורה',
  'פחח רכב': 'פחחות רכב',
  'פיזיותרפיסט': 'טיפול פיזיותרפי',
  'פנסיון כלבים': 'פנסיון לכלבים',
  'צבע': 'עבודות צביעה',
  'צבע לעבודות חיצוניות': 'צביעה חיצונית',
  'צלם': 'שירותי צילום',
  'קבלן איטום': 'עבודות איטום',
  'קבלן אלומיניום': 'עבודות אלומיניום',
  'קבלן בניה קלה': 'בנייה קלה',
  'קבלן גבס': 'עבודות גבס',
  'קבלן ממדים': 'עבודות ממ״ד',
  'קבלן מצבות': 'הקמת מצבות',
  'קבלן שיפוצים': 'שיפוצים',
  'קבלן שיש': 'עבודות שיש',
  'קבלן שלד': 'עבודות שלד',
  'קונסטרוקטור': 'תכנון קונסטרוקציה',
  'קוסמטיקאית': 'טיפולי קוסמטיקה',
  'רואה חשבון': 'ראיית חשבון',
  'רופא שיניים': 'טיפולי שיניים',
  'רפד': 'עבודות ריפוד',
  'רפלקסולוג': 'רפלקסולוגיה',
  'שיפוצניק': 'שיפוצים',
  'שמאי מקרקעין': 'שמאות מקרקעין',
  'שמאי רכב': 'שמאות רכב',
  'שמאי רכוש': 'שמאות רכוש',
  'שף פרטי': 'שירותי שף פרטי',
  'שרברב': 'שרברבות',
  'תקליטן ונגן': 'תקליטן ונגינה לאירועים',
};

/** Service noun used after "לגבי" / "בנושא" / "ל". Always grammatical. */
export function categoryService(title?: string | null): string {
  const n = (title || '').trim();
  if (!n) return '';
  return SERVICE_NOUN[n] || `שירותי ${n}`;
}

/** 2026-08-23 (user report): `ctx` (smartIntakeCtx) is ONE shared localStorage key across
 *  the whole site, not scoped per category — answering a plumbing questionnaire then
 *  browsing to an unrelated category's pro card (without re-running THAT category's own
 *  questionnaire) used to leak the plumbing-specific `cleanNeed`/`customNeed` straight into
 *  the electrician's message. This is deliberately narrow: it only compares category
 *  identity, so the intended reuse this same leftover ctx enables — same category, ANY
 *  other pro's WaSmartButton auto-prefilling from answers given anywhere else in the
 *  session — keeps working exactly as before; only a genuinely DIFFERENT category's stale
 *  need gets dropped back to the generic (still city-aware) message. */
export function ctxIsSameCategory(ctx: WaCtx, categoryName?: string | null): boolean {
  const currentTitle = categoryName?.trim();
  if (!currentTitle || !ctx?.categoryName) return true;
  return ctx.categoryName.trim() === currentTitle;
}

/** The canonical WhatsApp message text — ONE engine for every category.
 *  IMPORTANT:
 *   • Gender-neutral — pros may be male or female. Never use a second-person verb/adjective
 *     that conjugates by gender ("אתה פנוי", "תוכל"). Use "מה העלות ומתי זה אפשרי?".
 *   • Use the SERVICE noun (categoryService), not the professional title.
 *   • Timing/urgency is NOT written into the message — just ask "מה העלות ומתי זה אפשרי?".
 *  See [[experts-coil-business-rules]]. */
export function buildWaMessage(name: string, ctx: WaCtx, categoryName?: string | null): string {
  const title = categoryName?.trim() || ctx?.categoryName?.trim() || '';
  const service = categoryService(title);
  const sameCategory = ctxIsSameCategory(ctx, categoryName);

  // User wrote the request in their own words → use it verbatim (name swapped per expert).
  if (sameCategory && ctx?.customNeed && ctx.customNeed.trim()) {
    return `שלום ${name},\n${ctx.customNeed.trim()}\nתודה`;
  }

  if (sameCategory && ctx?.cleanNeed) {
    // City comes right after the service noun (e.g. "לשמאות מקרקעין בפתח תקווה") — it's
    // WHERE the service is needed, so it reads as part of the lead-in, not a trailing
    // afterthought; the specific-need clause (cleanNeed) follows after the dash.
    const cityPart = ctx.city ? ` ב${ctx.city}` : '';
    const needPart = ` – ${ctx.cleanNeed}`;
    // Timing intentionally omitted from the text — just ask "מה העלות ומתי זה אפשרי?".
    return `שלום ${name},\n${waOpeningLead(service)}${cityPart}${needPart}.\nמה העלות ומתי זה אפשרי?\nתודה`;
  }
  // City known but the "need" question not answered yet — still show a real, city-specific
  // message instead of falling through to the generic fallback below (previously this made
  // a city-only context indistinguishable from ctx=null).
  if (ctx?.city) {
    return `שלום ${name},\n${waOpeningLead(service)} ב${ctx.city}.\nמה העלות ומתי זה אפשרי?\nתודה`;
  }
  // Nothing answered yet — same opening lead the live preview types out, so what's sent
  // never diverges from what the user saw forming in the bubble. No emoji in the
  // greeting (2026-08-18) — see stripAstralEmoji()'s doc comment above.
  return `שלום ${name},\n${waOpeningLead(service)}.\nמה העלות ומתי זה אפשרי?\nתודה`;
}

/** A resolved chunk of text, or a marker for a clause still waiting on an unanswered
 *  question. Used ONLY for the on-page live-preview bubble (WaSmartButton) so it can
 *  render an animated "typing…" placeholder in place of the pending clause — the actual
 *  sent message always comes from `buildWaMessage`, which never emits placeholders. */
export type WaSegment = { text: string } | { pending: 'need' | 'city' };

/** Standard opening line for every category — greeting + lead-in, before anything is
 *  answered. ENGINE default: every category's WhatsApp preview starts here and animates
 *  in immediately, instead of a static "complete the questionnaire" placeholder. */
export function waOpeningLead(service: string): string {
  return service ? `אשמח לקבל הצעת מחיר ל${service}` : 'אשמח לקבל הצעת מחיר';
}

export function buildWaMessageSegments(name: string, ctx: WaCtx, categoryName?: string | null): WaSegment[] {
  const sameCategory = ctxIsSameCategory(ctx, categoryName);
  // Fully resolved cases — same text buildWaMessage would send, shown as one chunk.
  if (sameCategory && ctx?.customNeed && ctx.customNeed.trim()) return [{ text: buildWaMessage(name, ctx, categoryName) }];
  if (sameCategory && ctx?.cleanNeed) return [{ text: buildWaMessage(name, ctx, categoryName) }];

  const title = categoryName?.trim() || ctx?.categoryName?.trim() || '';
  const service = categoryService(title);
  const lead = waOpeningLead(service);

  // City known, "need" not answered yet — show the resolved lead-in and mark the rest pending.
  if (ctx?.city) {
    return [{ text: `שלום ${name},\n${lead} ב${ctx.city}` }, { pending: 'need' }];
  }

  // Nothing answered yet — show the greeting + lead-in right away (typing animation starts
  // immediately, before any question is answered), city held back as a "typing…" placeholder
  // instead of a static "complete the questionnaire" box. Standard opening for every category.
  // No emoji (2026-08-18) — this preview must match buildWaMessage()'s actual sent text.
  return [{ text: `שלום ${name},\n${lead} ב` }, { pending: 'city' }];
}

/** Topic used by the hidden tracking line. When the questionnaire supplied a short,
 *  specific need (cleanNeed), buildWaMessage() already spells it out verbatim in its own
 *  visible sentence (the " – {cleanNeed}." clause) — repeating it here would duplicate
 *  that exact text in the sent message, so this returns empty and withHiddenIntro() falls
 *  back to the plain "פונה אלייך מאתר המומחים" intro with no topic clause. Otherwise falls
 *  back to the category's service noun. Deliberately ignores customNeed — that's the
 *  user's own full sentence and already appears as its own line right after, so repeating
 *  it here would just duplicate it. */
function waTopic(ctx: WaCtx, service: string, sameCategory: boolean): string {
  if (sameCategory && ctx?.cleanNeed) return '';
  return service;
}

/** Inserts a line 2 that is sent to WhatsApp but NEVER shown in the on-site bubble preview —
 *  "פונה אלייך מאתר המומחים, לגבי {topic}". Call this ONLY when building the text that goes
 *  into the actual wa.me link; the bubble must keep rendering buildWaMessage()'s output as-is. */
export function withHiddenIntro(displayMessage: string, ctx: WaCtx, categoryName?: string | null): string {
  const title = categoryName?.trim() || ctx?.categoryName?.trim() || '';
  const service = categoryService(title);
  const topic = waTopic(ctx, service, ctxIsSameCategory(ctx, categoryName));
  const intro = topic ? `פונה אלייך מאתר המומחים, לגבי ${topic}` : 'פונה אלייך מאתר המומחים';
  const lines = displayMessage.split('\n');
  return [lines[0], intro, ...lines.slice(1)].join('\n');
}

/** Convenience: build the full message actually sent over WhatsApp (display message + hidden line 2). */
export function buildWaSendMessage(name: string, ctx: WaCtx, categoryName?: string | null): string {
  return withHiddenIntro(buildWaMessage(name, ctx, categoryName), ctx, categoryName);
}

// ── Perform the actual send/call — shared by ContactModal (first contact) and
// the contact buttons themselves (repeat clicks on an already-contacted pro,
// which re-fire the action directly without re-mounting ContactModal). ──────

/** Navigates to wa.me. 2026-09-09 fix (found live-tracing a visitor whose WhatsApp
 *  never opened and whose tracked link then sat at 0 clicks): this used to be
 *  `window.open(link, '_blank', 'noopener,noreferrer')`. Every real call site
 *  (lib/companyWa.ts's openCompanyWhatsApp, called from contactViaCompany() and
 *  ContactModal's handleSendToCompany) fires this AFTER `await`ing one or two
 *  /api/wa-links/single fetches — by the time it runs, the click handler is no longer
 *  inside the original synchronous user-gesture call stack, and Safari (iOS in
 *  particular, but not only) silently blocks a `window.open` popup opened outside that
 *  window — no error, the button just does nothing. A real top-level navigation
 *  (`location.href`, same pattern openCall() below already uses for tel:) is never
 *  subject to that popup-block check, async or not. Trades "stays on our tab" for
 *  "actually opens" — on mobile the visitor is leaving to the WhatsApp app either way. */
export function openWhatsApp(phone: string, text: string): void {
  const digits = (phone || '').replace(/^0/, '').replace(/\D/g, '');
  // stripAstralEmoji() here, not just at each message-composer's return — this is the
  // ONE place every client-side WhatsApp send funnels through (WaSmartButton,
  // ContactModal, lib/companyWa.ts), so it's the safety net even if a future template,
  // admin-typed bio, or DB-sourced text carries an emoji through untouched.
  // Duplicate guard (Founder 2026-09-28, ticket 13556) — the browser's one door to wa.me; a fix is reported to /api/track.
  const g = guardMessage(stripAstralEmoji(text)) as { text: string; fixed: string[] };
  if (g.fixed.length) { try { navigator.sendBeacon?.('/api/track', new Blob([JSON.stringify({ event: 'msg_duplicate_fixed', sessionId: getSessionId(), page: location.pathname + location.search, metadata: { fixed: g.fixed }, ...abCtx() })], { type: 'text/plain' })); /* 02/10: sessionId was missing → dropped */ } catch {} }
  const link = `https://wa.me/972${digits}?text=${encodeURIComponent(g.text)}`;
  waHandoff('open', {}); // 14/10/26 hand-off outcome (static import: a dynamic one would lose the race with the navigation)
  window.location.href = link;
}

export function openCall(phone: string) {
  window.location.href = `tel:${phone || ''}`;
}

// ── Verification-modal copy, by intent ──────────────────────────────────────
export type ContactIntent = 'whatsapp' | 'call' | 'default';

export type VerifyCopy = {
  headline: string;
  sub: string;
  phoneLabel: string;
  placeholder: string;
  btn: string;
  trust: string;
};

export function verifyCopy(intent: ContactIntent, name: string): VerifyCopy {
  const base = {
    phoneLabel: 'מספר הפלאפון שלך',
    placeholder: '05X-XXXXXXX',
    btn: 'שלח קוד אימות בחינם',
    trust: '🔒 השירות חינמי. הפרטים שלך נשמרים בסודיות.',
  };
  if (intent === 'whatsapp') {
    return {
      ...base,
      headline: 'כמעט שם! 💬',
      sub: `כדי לשלוח את ההודעה אל ${name} עלינו לאמת את מספר הפלאפון שלך, בדיוק כמו הביקורות הקודמות שעזרו לך לבחור מומחה.`,
    };
  }
  if (intent === 'call') {
    return {
      ...base,
      headline: 'כמעט שם! 💬',
      sub: `כדי להציג את מספר הטלפון של ${name} עלינו לאמת את מספר הפלאפון שלך, בדיוק כמו הביקורות הקודמות שעזרו לך לבחור מומחה.`,
    };
  }
  return {
    ...base,
    headline: 'כמעט שם! 💬',
    sub: `כדי להציג את פרטי הקשר של ${name} ולשמור על אמינות הביקורות, אמת את מספר הפלאפון שלך.`,
  };
}
